import * as React from "react";
import {
  Box, Button, TextField, Alert, Stack, Typography, MenuItem, Grid, Checkbox, FormControlLabel
} from "@mui/material";
import { createPatientIdentifier } from "../api/patientIdentifiersApi";

const ID_TYPES = ["UHID", "MRN", "NATIONAL_ID", "OTHER"];

const prettyError = (err) => {
  const data = err?.response?.data;
  if (!data) return err?.message || "Failed";
  if (typeof data === "string") return data;
  if (data.detail) return data.detail;
  try {
    return Object.entries(data)
      .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : String(v)}`)
      .join(" | ");
  } catch {
    return JSON.stringify(data);
  }
};

export default function PatientIdentifierForm() {
  const [form, setForm] = React.useState({
    patient_uhid: "",
    id_type: "UHID",
    id_value: "",
    is_primary: false,
  });

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [success, setSuccess] = React.useState("");

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    const uhid = form.patient_uhid.trim();
    if (!uhid) return setError("Patient UHID is required");
    if (!/^\d+$/.test(uhid)) return setError("Patient UHID must be numbers only");
    if (!form.id_value.trim()) return setError("ID value is required");

    const payload = {
      patient_uhid: uhid,        // ✅ number input
      id_type: form.id_type,
      id_value: form.id_value.trim(),
      is_primary: !!form.is_primary,
    };

    try {
      setLoading(true);
      const res = await createPatientIdentifier(payload);
      setSuccess(`✅ Identifier created for UHID: ${res.data.patient_uhid_out || uhid}`);
      setForm({ patient_uhid: "", id_type: "UHID", id_value: "", is_primary: false });
    } catch (err) {
      setError(prettyError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={submit} sx={{ maxWidth: 900, mx: "auto", p: { xs: 1, sm: 2 } }}>
      <Stack spacing={2}>
        <Typography variant="h6">Create Patient Identifier</Typography>
        {error && <Alert severity="error">{error}</Alert>}
        {success && <Alert severity="success">{success}</Alert>}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Patient UHID (numbers) *" value={form.patient_uhid} onChange={set("patient_uhid")} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField select fullWidth label="ID Type" value={form.id_type} onChange={set("id_type")}>
              {ID_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField fullWidth label="ID Value *" value={form.id_value} onChange={set("id_value")} />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.is_primary}
                  onChange={(e) => setForm((p) => ({ ...p, is_primary: e.target.checked }))}
                />
              }
              label="Primary"
            />
          </Grid>
        </Grid>

        <Button type="submit" variant="contained" disabled={loading}>
          {loading ? "Saving..." : "Save Identifier"}
        </Button>
      </Stack>
    </Box>
  );
}